import { Navigate, useRoutes, Outlet } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { useMediaQuery, useTheme } from '@mui/material';
import * as Sentry from '@sentry/react';
import { Notfound } from '../components/NotFound';
import { getCanonicalPartnerName, ServiceNationSolutionOrgName } from '../common/constants';
import LoginLayout from '../components/Authentication/LoginLayout';
import SignUpLayout from '../components/Authentication/SignUpLayout';
import SupportFAQ from '../components/Dashboard/SupportFAQ';
import SupportContact from '../components/Dashboard/SupportContact';
import AccountPreferences from '../components/Dashboard/Account/AccountPreferences';
import AccountSettings from '../components/Dashboard/Account/AccountSettings';
import AccountMemberships from '../components/Dashboard/Account/AccountMemberships';
import MyAccountDefaultRedirect from '../components/Dashboard/Account/MyAccountDefaultRedirect';
import ContentPreview from '../components/Dashboard/Content/ContentPreview';
import { DashboardHome } from '../components/Dashboard/DashboardHome';
import ContentHome from '../components/Dashboard/Content/ContentHome';
import {
  getAppDomain, JOIST_DOMAIN,
  SERVICE_NATION_BY_EVER_PRO_DOMAIN,
} from '../config/domains';
import { HDSetupInstructionsDetailView } from '../components/Dashboard/HDOnboardingFlow/HDSetupInstructionsDetailView';
import { useAppSelector } from '../store/hooks';
import {
  selectGetMemberInformationStatus,
  selectOnboardingLandingPageViewDone,
} from '../store/slices/onboardingSlice';
import { OnboardingLogin } from '../components/Authentication/OnboardingLogin';
import { useSolutionOrg } from '../config/useSolutionOrg';
import {
  ADVISORY_BOARDS_ROUTE,
  COACH_ROUTE,
  DESIGN_SERVICES_ROUTE,
  HOME_DEPOT_SETUP_OR_SKIP_ROUTE,
  HOME_DEPOT_SETUP_WEB,
  HOME_ROUTE,
  MORE_ROUTE,
  MYACCOUNT_CONTACT_US_ROUTE,
  MYACCOUNT_FAQ_ROUTE,
  MYACCOUNT_MEMBERSHIPS_ROUTE,
  MYACCOUNT_PREFERENCES_ROUTE,
  MYACCOUNT_ROUTE,
  MYACCOUNT_SETTINGS_ROUTE,
  MYACCOUNT_SOLUTION_ROUTE,
  PARTNERS_ROUTE,
  RESOURCES_ROUTE,
  REWARDS_ROUTE,
  SAVED_ROUTE,
  SEARCH_ROUTE,
  TRAINING_ROUTE,
  WALLET_ROUTE,
  AUTO_LOGIN_ROUTE,
} from './namedRoutes';
import CustomerSupportForm from '../common/CustomerSupportForm';
import { Partners } from '../components/Dashboard/Partner/Enrollment/Partners';
import { Wallet } from '../components/Dashboard/Wallet/Wallet';
import { Coach } from '../components/Coach/Coach';
import { ServiceNationIframePage } from '../components/Dashboard/ServiceNationIframe/ServiceNationIframePage';
import { ServiceNationIframeSkeleton } from '../components/Dashboard/ServiceNationIframe/ServiceNationIframeSkeleton';
import { useOptimizelyFlag } from '../common/hooks/useOptimizelyFlag';
import {
  AiCoachFlag,
  IframeSNEventTrainingPageFlag,
  IframeSNDesignServicesPageFlag,
  ServiceNationAdvisoryBoardsLinkFlag,
  ServiceNationDesignServicesLinkFlag,
  ServiceNationEventsLinkFlag,
  ServiceNationWalletFlag,
  UseEdgeHomeForServiceNationUsersFlag,
} from '../common/OptimizelyFlagsConstants';
import PersonalizeContent from '../components/PersonalizeContent';
import AccountJoist from '../components/Dashboard/Account/AccountJoist';
import { HomeWrapper } from '../components/HomeWrapper';
import { OnboardHomeWrapper } from '../components/Onboarding/Home/OnboardHomeWrapper';
import { OnboardHDHomeWebWrapper } from '../components/Onboarding/Home/OnboardHDHomeWebWrapper';
import { OnboardHDHomeWrapper } from '../components/Onboarding/Home/OnboardHDHomeWrapper';
import { ContentViewMoreWrapper } from '../components/Dashboard/Content/ContentViewMoreWrapper';
import { SavedContent } from '../components/Dashboard/Content/SavedContent';
import { ContentSearch } from '../components/Dashboard/Content/ContentSearch';
import { ContentArticleView } from '../components/Dashboard/Content/ContentArticleView';
import { Dashboard } from '../components/Dashboard/Dashboard';
import PartnerProfileComponent from '../components/Dashboard/Partner/IndividualPages/PartnerProfileComponent';
import { usePartnerConfigList } from '../components/Dashboard/Partner/Enrollment/usePartnerConfigList';
import SNForwardComponent from '../components/Authentication/SNForwardComponent';
import { toSlug } from '../common/utils/urlUtils';
import { TestIds } from '../assets/testIds';
import { AccessResource, useHasAccess } from '../common/hooks/useHasAccess';

const useSentryRoutes = Sentry.wrapUseRoutes(useRoutes);

// Gates the AI Coach page behind the `aiCoach` Optimizely flag so it cannot be
// reached by direct URL when the flag is off. Shows the loader until the
// decision resolves, then NotFound if the flag is disabled.
// Fails closed: if Optimizely could not decide at all (client error/timeout),
// the page stays gated rather than being exposed on an unresolved decision.
const AiCoachRouteGuard = (
  { children }: { children: JSX.Element },
): JSX.Element => {
  const { decision, isDecisionReady, loaderComponent } = useOptimizelyFlag({
    flag: AiCoachFlag,
  });
  if (!isDecisionReady) {
    return loaderComponent;
  }
  return decision?.enabled ? children : <Notfound />;
};

// Controls Wallet page access for Service Nation members.
// - Non-SN members: always allowed
// - SN members (any tier, including free/EDGE): gated by the serviceNationWallet
//   feature flag, and (SN-190) by SN's historic_rebate_access grant — an SN
//   member without that grant must not see the Wallet page at all, regardless
//   of tier or master/sub-account status.
const WalletRouteGuard = (
  { children }: { children: JSX.Element },
): JSX.Element => {
  const { decision, isDecisionReady, loaderComponent } = useOptimizelyFlag({
    flag: ServiceNationWalletFlag,
  });
  const { solutionOrgName } = useSolutionOrg();
  const isServiceNation = solutionOrgName === ServiceNationSolutionOrgName;
  const getMemberInformationStatus = useAppSelector(selectGetMemberInformationStatus);
  const hasAccess = useHasAccess(AccessResource.WalletPage, {
    serviceNationWalletFlag: decision?.enabled,
  });

  if (!isDecisionReady) return loaderComponent;

  // (SN-190) historic_rebate_access is stripped from redux-persist storage, so it
  // is only ever known once getMemberInformation resolves. DashboardHome's loading
  // gate only blocks when there is no persisted `company` at all, so a returning SN
  // member can reach here with a rehydrated (stale) company but no fresh access
  // grant yet - wait for that fetch instead of treating "not loaded yet" as "denied".
  if (isServiceNation && getMemberInformationStatus === 'pending') return loaderComponent;

  return hasAccess ? children : <Notfound />;
};

// Gates the embedded Training (Service Nation events) page behind the
// `iframeSNEventTrainingPage` flag so it cannot be reached by direct URL when the
// flag is off, and applies the same access rules as the Training nav item.
// Fails closed while any decision is unresolved.
const TrainingRouteGuard = (
  { children }: { children: JSX.Element },
): JSX.Element => {
  const {
    decision: iframeDecision,
    isDecisionReady: isIframeDecisionReady,
  } = useOptimizelyFlag({ flag: IframeSNEventTrainingPageFlag });
  const {
    decision: eventsLinkDecision,
    isDecisionReady: isEventsLinkDecisionReady,
  } = useOptimizelyFlag({ flag: ServiceNationEventsLinkFlag });
  const {
    decision: useEdgeHomeForSNDecision,
    isDecisionReady: isUseEdgeHomeForSNDecisionReady,
  } = useOptimizelyFlag({ flag: UseEdgeHomeForServiceNationUsersFlag });
  const hasAccess = useHasAccess(AccessResource.EventsPage, {
    useEdgeHomeForSNFlag: useEdgeHomeForSNDecision?.enabled,
  });

  if (!isIframeDecisionReady || !isEventsLinkDecisionReady || !isUseEdgeHomeForSNDecisionReady) {
    return <ServiceNationIframeSkeleton testId={TestIds.trainingPage.skeleton} />;
  }

  return iframeDecision?.enabled && eventsLinkDecision?.enabled && hasAccess ?
    children : <Notfound />;
};

// Gates the embedded Design Services page behind the `iframeSNDesignServicesPage` flag
// so it cannot be reached by direct URL when the flag is off, and applies the same
// access rules as the Design Services nav item. Fails closed while any decision is unresolved.
const DesignServicesRouteGuard = (
  { children }: { children: JSX.Element },
): JSX.Element => {
  const {
    decision: iframeDecision,
    isDecisionReady: isIframeDecisionReady,
  } = useOptimizelyFlag({ flag: IframeSNDesignServicesPageFlag });
  const {
    decision: designServicesLinkDecision,
    isDecisionReady: isDesignServicesLinkDecisionReady,
  } = useOptimizelyFlag({ flag: ServiceNationDesignServicesLinkFlag });
  const {
    decision: useEdgeHomeForSNDecision,
    isDecisionReady: isUseEdgeHomeForSNDecisionReady,
  } = useOptimizelyFlag({ flag: UseEdgeHomeForServiceNationUsersFlag });
  const hasAccess = useHasAccess(AccessResource.DesignServicesPage, {
    useEdgeHomeForSNFlag: useEdgeHomeForSNDecision?.enabled,
  });

  if (!isIframeDecisionReady || !isDesignServicesLinkDecisionReady ||
    !isUseEdgeHomeForSNDecisionReady) {
    return <ServiceNationIframeSkeleton testId={TestIds.designServicesPage.skeleton} />;
  }

  return iframeDecision?.enabled && designServicesLinkDecision?.enabled && hasAccess ?
    children : <Notfound />;
};

// Gates the embedded Advisory Boards page behind the `serviceNationAdvisoryBoardsLink`
// flag so it cannot be reached by direct URL when the flag is off, and applies the same
// access rules as the Advisory Boards nav item. Fails closed while the decision is unresolved.
const AdvisoryBoardsRouteGuard = (
  { children }: { children: JSX.Element },
): JSX.Element => {
  const { decision, isDecisionReady } = useOptimizelyFlag({
    flag: ServiceNationAdvisoryBoardsLinkFlag,
  });
  const hasAccess = useHasAccess(AccessResource.AdvisoryBoardsPage);

  if (!isDecisionReady) {
    return <ServiceNationIframeSkeleton testId={TestIds.advisoryBoardsPage.skeleton} />;
  }

  return decision?.enabled && hasAccess ? children : <Notfound />;
};

// Memberships (the upgrade page) is only for Roundtable and below, so higher tiers
// cannot reach it by direct URL either.
const MembershipsRouteGuard = (
  { children }: { children: JSX.Element },
): JSX.Element => (
  useHasAccess(AccessResource.MembershipsPage) ? children : <Notfound />
);

export function NavigationRoutes() {
  const onboardingLandingPageViewDone = useAppSelector(selectOnboardingLandingPageViewDone);
  const { isNativeAppExperience, enrollOnSplashScreen } = useSolutionOrg();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  // determine current domain of app
  const appDomain = getAppDomain();

  const partnerConfigList = usePartnerConfigList();

  const {
    decision: useEdgeHomeForServiceNationUsersDecision,
    isDecisionReady: isUseEdgeHomeForServiceNationUsersDecisionReady,
    loaderComponent: useEdgeHomeForServiceNationUsersLoaderComponent,
  } = useOptimizelyFlag({
    flag: UseEdgeHomeForServiceNationUsersFlag,
  });

  const hasAccountPageAccess = useHasAccess(AccessResource.AccountPage, {
    useEdgeHomeForSNFlag: useEdgeHomeForServiceNationUsersDecision?.enabled,
  });

  const myAccountRouteChildren = !hasAccountPageAccess ? [] : [
    {
      index: true, // This indicates that this is the default route for the parent path
      element: <MyAccountDefaultRedirect />, // Dynamically redirect based on feature flags
    },
    {
      path: MYACCOUNT_MEMBERSHIPS_ROUTE,
      element: <MembershipsRouteGuard><AccountMemberships /></MembershipsRouteGuard>,
    },
    {
      path: MYACCOUNT_SETTINGS_ROUTE,
      element: <AccountSettings />,
    },
    {
      path: MYACCOUNT_PREFERENCES_ROUTE,
      element: <AccountPreferences />,
    },
    {
      path: MYACCOUNT_FAQ_ROUTE,
      element: <SupportFAQ />,
    },
    {
      path: MYACCOUNT_CONTACT_US_ROUTE,
      element: <SupportContact />,
    },
    ...appDomain === JOIST_DOMAIN ? [{
      path: MYACCOUNT_SOLUTION_ROUTE,
      element: <AccountJoist />,
    }] : [],
  ];

  const sharedRoutes = [
    {
      path: '/',
      element: <DashboardHome />,
      children: [
        {
          path: HOME_ROUTE,
          element: <Dashboard />,
        },
        {
          path: RESOURCES_ROUTE,
          element: <ContentHome />,
        },
        {
          path: SAVED_ROUTE,
          element: <SavedContent />,
        },
        {
          path: REWARDS_ROUTE,
          element: <Navigate to='/partners' />,
        },
        {
          path: WALLET_ROUTE,
          element: <WalletRouteGuard><Wallet /></WalletRouteGuard>,
        },
        {
          path: COACH_ROUTE,
          element: <AiCoachRouteGuard><Coach /></AiCoachRouteGuard>,
        },
        {
          path: TRAINING_ROUTE,
          element: (
            <TrainingRouteGuard>
              <ServiceNationIframePage
                destination="events"
                translationKey="trainingPage"
                testIds={TestIds.trainingPage}
              />
            </TrainingRouteGuard>
          ),
        },
        {
          path: DESIGN_SERVICES_ROUTE,
          element: (
            <DesignServicesRouteGuard>
              <ServiceNationIframePage
                destination="resource-hub"
                translationKey="designServicesPage"
                testIds={TestIds.designServicesPage}
              />
            </DesignServicesRouteGuard>
          ),
        },
        {
          path: ADVISORY_BOARDS_ROUTE,
          element: (
            <AdvisoryBoardsRouteGuard>
              <ServiceNationIframePage
                destination="advisory-boards"
                translationKey="advisoryBoardsPage"
                testIds={TestIds.advisoryBoardsPage}
              />
            </AdvisoryBoardsRouteGuard>
          ),
        },
        {
          path: MYACCOUNT_ROUTE,
          // eslint-disable-next-line no-nested-ternary
          element: !isUseEdgeHomeForServiceNationUsersDecisionReady ?
            useEdgeHomeForServiceNationUsersLoaderComponent :
            (hasAccountPageAccess ? <Outlet /> : <Notfound />),
          children: myAccountRouteChildren,
        },
        {
          path: SEARCH_ROUTE,
          element: <ContentSearch />,
        },
        {
          path: MORE_ROUTE,
          element: <ContentViewMoreWrapper />,
        },
        {
          path: PARTNERS_ROUTE,
          children: [
            {
              index: true,
              element: <Partners />,
            },
            ...partnerConfigList.map((partner) => ({
              // Register the route on the rename-stable canonical slug so it stays
              // in sync with the profile links (usePartnerConfigList) and resolver
              // (usePartnerProfile); a partner rename no longer breaks the route.
              path: toSlug(getCanonicalPartnerName(partner.partnerCode, partner.partnerName)),
              element: <PartnerProfileComponent />,
            })),
          ],
        },
        {
          path: 'content/:id',
          element: <ContentArticleView />,
        },
      ],
    },
    {
      path: 'personalized-content',
      element: <PersonalizeContent />,
    },
    {
      path: '/onboarding',
      // eslint-disable-next-line no-nested-ternary
      element: (!onboardingLandingPageViewDone || enrollOnSplashScreen) ? (isDesktop ? <AnimatePresence mode="wait">
        <HomeWrapper key={'/onboarding'} />
      </AnimatePresence> : <HomeWrapper />) : <Navigate to='/onboarding/form' />,
    },
    {
      path: '/onboarding/form',
      element: isDesktop ? (<AnimatePresence mode="wait">
        <OnboardHomeWrapper key={'/onboarding/form'} />
      </AnimatePresence>
      ) : <OnboardHomeWrapper />,
    },
    {
      path: HOME_DEPOT_SETUP_OR_SKIP_ROUTE,
      element: isNativeAppExperience ?
        <OnboardHDHomeWrapper /> : <Notfound />,
    },
    {
      path: HOME_DEPOT_SETUP_WEB,
      // eslint-disable-next-line no-nested-ternary
      element: !isNativeAppExperience ? <OnboardHDHomeWebWrapper /> : <Notfound />,
    },
    {
      path: '/onboarding/login',
      element: <OnboardingLogin />,
    },
    {
      path: '/login',
      element: <LoginLayout />,
    },
    {
      path: AUTO_LOGIN_ROUTE,
      element: <LoginLayout />,
    },
    {
      path: '/signup',
      element: appDomain === SERVICE_NATION_BY_EVER_PRO_DOMAIN ? <SignUpLayout /> : <Notfound />,
    },
    {
      path: '/service-nation-forward',
      element:
        appDomain === SERVICE_NATION_BY_EVER_PRO_DOMAIN ? <SNForwardComponent /> : <Notfound />,
    },
    {
      path: '/contact-support',
      element: <CustomerSupportForm />,
    },
    {
      path: '/home/preview/:id',
      children: [
        {
          index: true, // This indicates that this is the default route for the parent path
          element: <ContentPreview />,
        },
        {
          path: 'detail',
          element: <ContentArticleView />,
        },
      ],
    },
    {
      path: '/paypal-verification',
      element: <Navigate to={WALLET_ROUTE} />,
    },
    {
      path: '/verification',
      element: <Navigate to={WALLET_ROUTE} />,
    },
  ];

  sharedRoutes.push({
    path: '/home/hdInstruction',
    element: <HDSetupInstructionsDetailView />,
  });

  // generate the selected routes
  const selectedRoutes = [
    ...sharedRoutes,
    {
      path: '*',
      element: <Notfound />,
    },
  ];

  const routesComponent = useSentryRoutes(selectedRoutes);

  return routesComponent;
}
